import { useEffect, useState } from 'react';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_SECONDARY_SM } from '../buttonStyles';
import { TEXT_ACTION } from './surfaces';
import { RequestNewTime } from './RequestNewTime';
import { busyStyle } from '../../utils/busyStyle';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { subscribeToBooking } from '../../services/liveCallBookings';
import { answerLiveLessonTimeProposal, liveLessonsErrorMessage } from '../../services/liveLessons';
import { firstNameOf } from '../../utils/liveLessonText';
import { lessonProposalLine } from '../../utils/lessonTimes';
import type { Message } from '../../types/chat';
import type { LiveCallBooking } from '../../types/liveCall';
import { completeOptimistically } from '../../services/optimistic';
import { movedPatch } from '../../utils/optimisticLessons';

/**
 * The coach's new times for one lesson, in the thread (decision 113;
 * docs/booking-contracts.md 6): the times as chips, "Keep mine" and "Suggest
 * others" (decision 123). A chip moves the lesson with one tap; Suggest
 * others opens the player's open-time picker, which moves it at once. A chip past its expiresAt is
 * drawn disabled. The server updates the card in place; only an open card
 * is tappable, and an answered one says what happened.
 */
export function LessonTimeProposalBubble({ message }: { message: Message }) {
  const sent = message.lessonTimeProposal!;
  // A tapped time is the card's answer at once (speed.md 2): accepted, that time chosen, the server behind it.
  const [local, setLocal] = useState<{ status: 'accepted'; chosen: number } | null>(null);
  const p = local && sent.status === 'open' ? { ...sent, ...local } : sent;
  const { currentUser } = useAuth();
  const { showError } = useToast();
  const tz = useViewerZone();
  const [b, setB] = useState<LiveCallBooking | null | undefined>(undefined);
  useEffect(() => subscribeToBooking(p.bookingId, setB), [p.bookingId]);
  const player = useUserIdentity(b?.playerId, 'player');
  const coach = useUserIdentity(b?.coachId, 'coach');
  const [busy, setBusy] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [now] = useState(() => Date.now());
  const isCoach = !!b && currentUser?.uid === b.coachId;
  const playerFirst = firstNameOf(player?.name ?? b?.playerName, 'Your player');
  const label = (t: (typeof p.times)[number]) => (isCoach ? t.coachLabel : t.label);
  const chosen = p.chosen != null ? p.times[p.chosen] : null;
  const keptDay = new Date(p.fromStartAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
  const line = lessonProposalLine({ status: p.status, chosenLabel: chosen ? label(chosen) : null, keptDay, isCoach, playerFirst });
  const headline = isCoach ? `You suggested new times for ${p.lesson}` : message.text || `New times for ${p.lesson}`;
  // Tappable once the lesson has said this is the player's card.
  const open = p.status === 'open' && !!b && !isCoach;

  const answer = async (key: string, a: { answer: 'accept' | 'keep' | 'pick'; time?: number }) => {
    // Accept a proposal completes on the device (speed.md 2, decision 157): the card says it is picked and the lesson
    // moves at once; answerLiveLessonTimeProposal runs behind with one requestId; a toast only if every try fails.
    if (a.answer === 'accept' && a.time != null && b) {
      const i = a.time;
      setLocal({ status: 'accepted', chosen: i });
      completeOptimistically(p.bookingId, movedPatch(b, p.times[i].startAt, tz),
        (requestId) => answerLiveLessonTimeProposal({ proposalId: p.proposalId, ...a, requestId }),
        { keepLines: true, onFail: (err) => { setLocal(null); showError(liveLessonsErrorMessage(err, "Couldn't move the lesson. Try again.")); } });
      return;
    }
    setBusy(key);
    try {
      await answerLiveLessonTimeProposal({ proposalId: p.proposalId, ...a });
      if (a.answer === 'pick') setPicking(true);
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not answer these times. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="my-3 flex justify-center" data-lesson-time-proposal={p.status}>
      <div className="w-full max-w-[320px] rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-3.5">
        <div className="text-[13px] leading-[18px] text-db-text-2">{headline}</div>
        {open ? (
          <>
            <div className="mt-3 flex flex-col gap-2">
              {p.times.map((t, i) => {
                const expired = (t.expiresAt ?? t.startAt) <= new Date(now).toISOString();
                return (
                  <button key={t.startAt} type="button" disabled={!!busy || expired} aria-busy={busy === `t${i}`}
                    style={busyStyle(busy === `t${i}`)} onClick={() => { void answer(`t${i}`, { answer: 'accept', time: i }); }}
                    data-proposal-time
                    className="ios-press relative inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-[100px] bg-[rgba(26,107,138,0.12)] px-4 text-[15px] leading-[20px] font-medium text-biscayne focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 disabled:cursor-not-allowed disabled:bg-[rgba(120,120,128,0.12)] disabled:text-db-text-3">
                    {busy === `t${i}` ? <ButtonSpinner size={16} /> : t.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <button type="button" disabled={!!busy} aria-busy={busy === 'keep'} style={busyStyle(busy === 'keep')}
                onClick={() => { void answer('keep', { answer: 'keep' }); }} className={BTN_SECONDARY_SM}>
                {/* Decision 123's words, not the server's "Keep Nov 4" and "Pick something else". */}
                {busy === 'keep' ? <ButtonSpinner size={16} /> : 'Keep mine'}
              </button>
              {/* Suggest others: the player's own picker, which moves the lesson with this proposal (booking-contracts.md 11). */}
              <button type="button" disabled={!!busy} onClick={() => { void answer('pick', { answer: 'pick' }); }} className={TEXT_ACTION}>
                Suggest others
              </button>
            </div>
          </>
        ) : (
          <>
            {/* The coach sees what was sent, in their own zone, until it is answered. */}
            {isCoach && p.status === 'open' && (
              <ul className="mt-1">
                {p.times.map((t) => (
                  <li key={t.startAt} className="text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">{label(t)}</li>
                ))}
              </ul>
            )}
            {line && <div className="mt-2 text-[15px] leading-[20px] text-db-text-1">{line}</div>}
          </>
        )}
      </div>
      {picking && b && (
        <RequestNewTime title="Request a New Time" booking={b} isCoach={false} tz={tz} proposalId={p.proposalId}
          otherName={coach?.name ?? b.coachName ?? ''} otherPhoto={coach?.avatarUrl} onClose={() => setPicking(false)} />
      )}
    </div>
  );
}
